import { usd as usdCentavos } from '../LeadsDashboard/format';
import type { PeriodoBot } from '@/services/api/analytics-bot';

/**
 * Formatação do Analytics do Bot (guia §7). `null` é "dado inexistente" e vira
 * traço — diferente de zero. NaN/Infinity também viram traço.
 */

export const TRACO = '—';

const NF = new Intl.NumberFormat('pt-BR');
const ok = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v);

export const n = (v: number | null | undefined): string => (ok(v) ? NF.format(Math.round(v)) : TRACO);

/** Percentual já em 0-100. */
export const pct = (v: number | null | undefined, casas = 2): string =>
    ok(v) ? `${v.toFixed(casas).replace('.', ',')}%` : TRACO;

/** Centavos -> "US$ 1.234,56". */
export const usd = (centavos: number | null | undefined): string => (ok(centavos) ? usdCentavos(centavos) : TRACO);

/** RPC/RPS: dólar com 4 casas — com 2 casas viram todos US$ 0,00. */
export const usd4 = (dolar: number | null | undefined): string =>
    ok(dolar)
        ? `US$ ${dolar.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`
        : TRACO;

/** Divisão protegida para os poucos agregados que o front monta (ramos, % do total). */
export const div = (a: number | null | undefined, b: number | null | undefined): number | null =>
    ok(a) && ok(b) && b !== 0 ? a / b : null;

/** "aguarda X" a partir de segundos. */
export const espera = (seg: number | null | undefined): string => {
    if (!ok(seg)) return TRACO;
    if (seg <= 0) return 'imediato';
    if (seg < 60) return `${Math.round(seg)} s`;
    if (seg < 3600) return `${Math.round(seg / 60)} min`;
    if (seg < 86400) {
        const h = Math.floor(seg / 3600);
        const m = Math.round((seg % 3600) / 60);
        return m ? `${h} h ${m} min` : `${h} h`;
    }
    const d = Math.floor(seg / 86400);
    const h = Math.round((seg % 86400) / 3600);
    return h ? `${d} d ${h} h` : `${d} d`;
};

/** Mesma regra do backend: tira http(s):// e corta no primeiro "/". */
export const dominioBase = (valor: string | null | undefined): string | null => {
    if (!valor) return null;
    const base = valor.replace('https://', '').replace('http://', '').split('/')[0];
    return base || null;
};

export const PERIODOS: { k: PeriodoBot; l: string; dias: number }[] = [
    { k: 'hoje', l: 'Hoje', dias: 1 },
    { k: 'ontem', l: 'Ontem', dias: 1 },
    { k: '7d', l: '7 dias', dias: 7 },
    { k: '14d', l: '14 dias', dias: 14 },
    { k: '30d', l: '30 dias', dias: 30 },
    { k: 'mes', l: 'Este mês', dias: 31 },
    { k: 'custom', l: 'Período', dias: 0 },
];

/** Teto do período personalizado; o backend recusa acima disso. */
export const MAX_DIAS_CUSTOM = 31;

const isoBrt = (d: Date): string =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(d);

export const somaDias = (iso: string, dias: number): string => {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + dias));
    return dt.toISOString().slice(0, 10);
};

/**
 * Intervalo do período em dias de Brasília, inclusivo. Só é usado enquanto o
 * /resumo do período atual não chegou; depois vale o que o backend devolveu.
 */
export const intervaloLocal = (periodo: PeriodoBot): { de: string; ate: string } => {
    const hoje = isoBrt(new Date());
    switch (periodo) {
        case 'ontem': {
            const ontem = somaDias(hoje, -1);
            return { de: ontem, ate: ontem };
        }
        case '7d': return { de: somaDias(hoje, -6), ate: hoje };
        case '14d': return { de: somaDias(hoje, -13), ate: hoje };
        case '30d': return { de: somaDias(hoje, -29), ate: hoje };
        case 'mes': return { de: `${hoje.slice(0, 8)}01`, ate: hoje };
        default: return { de: hoje, ate: hoje };
    }
};

export const dataCurta = (iso: string): string => {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
};

/** Ordena com null sempre no fim, nos dois sentidos. */
export const comparaNulo = (a: number | null | undefined, b: number | null | undefined, desc: boolean): number => {
    const va = ok(a) ? a : null;
    const vb = ok(b) ? b : null;
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    return desc ? vb - va : va - vb;
};
